import { ButtonV2, TooltipV2 } from '@cimplico/components';
import { PlusIcon, TrashIcon } from '@heroicons/react/20/solid';
import { isField, isList, isListItem, isTable } from '../store/utils';
import { getOffset, getZIndex } from '../utils';
import { ReactComponent as ChipDownIcon } from '../../../../assets/chip-down.svg';
import { ReactComponent as TackIcon } from '../../../../assets/tack.svg';
import {
    CWTSchemaPropertiesSummary,
    CWTSchemaPropertySummary,
    WorksheetSchemaBlockProperty,
    WorksheetSchemaField,
} from '../../../../types/custom-worksheet-v2';
import { getReference } from '../../../../components/custom-worksheet/utils';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCollapseAll, selectCurrentFocusField, selectShowHidden } from '../store/selectors';
import { setCollapseAll } from '../store/slice';
import PropertyProgress from '../PropertyProgress';
import CWPropertyName from '../../../../components/custom-worksheet/CWPropertyName';
import CWField from './field/CWField';
import SummaryField from './section/SummaryField';
import plural from '../../../../utilities/plural';
import { useIsMutating } from 'react-query';
import { useLocation } from 'react-router-dom';

type PropertyHeaderProps = {
    property: WorksheetSchemaBlockProperty;
    pinnedField?: WorksheetSchemaField | CWTSchemaPropertiesSummary | null;
    headingSize: 'sm' | 'base' | 'lg' | 'xl' | '2xl';
    isCollapsed?: boolean;
    selectedRows?: string[];
    onCollapse?: (isCollapsed: boolean) => void;
    onAdd?: () => void;
    onRemove?: () => void;
};

function PropertyHeader({
    property,
    pinnedField,
    headingSize,
    isCollapsed,
    selectedRows,
    onCollapse,
    onAdd,
    onRemove,
}: PropertyHeaderProps): JSX.Element {
    const dispatch = useDispatch();
    const collapseAll = useSelector(selectCollapseAll);
    const currentFocusField = useSelector(selectCurrentFocusField);
    const showHidden = useSelector(selectShowHidden);
    const isMutating = useIsMutating({ mutationKey: ['updateCustomWorksheetSchema'] });
    const location = useLocation();
    const renderedInWorksheet = location.pathname.includes('trial-balance') || location.pathname.includes('other-schedules');
    // Only set this when inside a worksheet
    const stickyScrollHeaderHight = renderedInWorksheet ? 60 : 0;
    const offset = getOffset(property.id) + stickyScrollHeaderHight;
    const zIndex = getZIndex(property.id);

    const canCollapse = isCollapsed !== undefined && onCollapse !== undefined;

    useEffect(() => {
        if (currentFocusField && currentFocusField.id.includes(property.id)) {
            if (collapseAll !== undefined) {
                dispatch(setCollapseAll(undefined));
            }
            onCollapse && onCollapse(false);
        }
    }, [collapseAll, currentFocusField, dispatch, onCollapse, property.id]);

    function handleCollapse(): void {
        if (collapseAll !== undefined) {
            dispatch(setCollapseAll(undefined));
        }
        onCollapse && onCollapse(!isCollapsed);
    }

    return (
        <div className="py-1 sticky w-full bg-white" style={{ zIndex, top: offset }}>
            <div className="flex items-center gap-4">
                <div className="flex gap-2 items-center">
                    {canCollapse && (
                        <ChipDownIcon
                            className={`${isCollapsed ? '-rotate-90' : ''} transition-all cursor-pointer`}
                            onClick={handleCollapse}
                        />
                    )}
                    <CWPropertyName property={property} headingSize={headingSize} canRename={isListItem(property)} />
                    <PropertyProgress property={property} />
                </div>
                {selectedRows && isTable(property) && onRemove && (
                    <>
                        {selectedRows.length > 0 ? (
                            <ButtonV2
                                size="xs"
                                type="secondary"
                                label={`Remove (${selectedRows.length})`}
                                onClick={onRemove}
                                isDisabled={property.properties.length - selectedRows.length < (property.config?.minRows || 0)}
                                disabledHint={`This table requires a minimum of ${property.config?.minRows} ${plural(
                                    property.config?.minRows ?? 0,
                                    'row'
                                )}`}
                            />
                        ) : onAdd ? (
                            <TooltipV2 message="New row">
                                <ButtonV2 type="secondary" size="xs" leftIcon={<PlusIcon />} onClick={onAdd} />
                            </TooltipV2>
                        ) : (
                            <></>
                        )}
                    </>
                )}
                {canCollapse && <div className="h-[1px] bg-gray-200 grow" />}
                {isCollapsed && pinnedField && (
                    <>
                        {'pinned' in pinnedField && isField(pinnedField) && (pinnedField.hidden ? showHidden : true) && (
                            <div className="-my-2 w-[300px] mr-0.5">
                                <div className="flex gap-1 items-center">
                                    {pinnedField.pinned && (
                                        <TackIcon style={{ fill: pinnedField.pinned ? '#16A249' : undefined }} className="shrink-0" />
                                    )}
                                    <CWField field={pinnedField} parentId={property.id} />
                                </div>
                            </div>
                        )}
                        {!('pinned' in pinnedField) && pinnedField.properties[getReference(property.id)] && (
                            <div className="-my-2 w-[300px] mr-0.5">
                                <SummaryField
                                    showLabel={false}
                                    summary={pinnedField.properties[getReference(property.id)] as CWTSchemaPropertySummary}
                                />
                            </div>
                        )}
                    </>
                )}
                {onAdd && isList(property) && (
                    <TooltipV2 message="Add list item" position="left">
                        <ButtonV2 type="secondary" size="xs" leftIcon={<PlusIcon />} label="New" onClick={onAdd} />
                    </TooltipV2>
                )}
                {onRemove && isListItem(property) && (
                    <div className="grow flex justify-end">
                        <TooltipV2 message="Delete list item" position="left">
                            <ButtonV2 type="danger" size="sm" leftIcon={<TrashIcon />} onClick={onRemove} />
                        </TooltipV2>
                    </div>
                )}
            </div>
            {property.description && <p className={`text-sm text-gray-600 ${canCollapse ? 'pl-6' : ''}`}>{property.description}</p>}
        </div>
    );
}

export default PropertyHeader;
